import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Clock, Save, ChevronLeft, ChevronRight, AlertTriangle, X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import ErrorDisplay from '../../components/common/ErrorDisplay'
import ExamInstructions from '../../components/exam/ExamInstructions'
import { useExam } from '../../hooks/useExams'
import { examService } from '../../services/examService'
import ObjectiveQuestion from '../../components/exam/ObjectiveQuestion'
import TheoryQuestion from '../../components/exam/TheoryQuestion'
import FillInGapsQuestion from '../../components/exam/FillInGapsQuestion'

const TakeExam = () => {
  const { examId } = useParams()
  const navigate = useNavigate()
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [examSession, setExamSession] = useState(null)
  const [showSubmitDialog, setShowSubmitDialog] = useState(false)
  const [showInstructions, setShowInstructions] = useState(true)
  const [examStarted, setExamStarted] = useState(false)
  const [showExitWarning, setShowExitWarning] = useState(false)

  const { 
    exam, 
    loading, 
    error,
    hasAttempted,
    attemptLoading
  } = useExam(examId)

  // Initialize exam session when exam starts
  useEffect(() => {
    if (exam && examStarted && !examSession) {
      try {
        // Start exam session
        setExamSession({ examId, startTime: new Date() })
        setTimeRemaining(exam.duration * 60) // Convert minutes to seconds
      } catch (err) {
        console.error('Failed to start exam:', err)
      }
    }
  }, [exam, examId, examSession, examStarted])

  // Timer countdown
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Auto-submit when time runs out
            handleSubmitExam()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [timeRemaining])

  // Auto-save answers every 30 seconds (disabled for now - save-progress API not implemented)
  // useEffect(() => {
  //   const autoSaveInterval = setInterval(() => {
  //     if (Object.keys(answers).length > 0) {
  //       handleAutoSave()
  //     }
  //   }, 30000) // 30 seconds

  //   return () => clearInterval(autoSaveInterval)
  // }, [answers])

  // Warn before leaving page and prevent accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (examStarted) {
        e.preventDefault()
        e.returnValue = 'Are you sure you want to leave? Your progress will be saved but the exam timer will continue.'
      }
    }

    const handleKeyDown = (e) => {
      // Prevent F5, Ctrl+R, Ctrl+F5
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        e.preventDefault()
        alert('Please do not refresh the page during the exam!')
      }
      
      // Prevent Alt+Tab, Ctrl+Tab, etc.
      if (e.altKey || (e.ctrlKey && e.key === 'Tab')) {
        e.preventDefault()
      }
    }

    if (examStarted) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      document.addEventListener('keydown', handleKeyDown)
      
      // Disable right-click context menu
      document.addEventListener('contextmenu', (e) => e.preventDefault())
      
      // Disable text selection
      document.addEventListener('selectstart', (e) => e.preventDefault())
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('contextmenu', (e) => e.preventDefault())
      document.removeEventListener('selectstart', (e) => e.preventDefault())
    }
  }, [examStarted])


  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }))
  }


  const handleNextQuestion = () => {
    if (exam && currentQuestionIndex < (exam.questionPaper?.questions?.length || 0) - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    }
  }

  const handleGoToQuestion = (index) => {
    setCurrentQuestionIndex(index)
  }

  const handleStartExam = async () => {
    try {
      // Start exam session on the server
      await examService.startExam(examId);
      
      setShowInstructions(false)
      setExamStarted(true)
    } catch (err) {
      console.error('Failed to start exam:', err);
      
      // Check if it's the "already taken" error
      if (err.response?.data?.message?.includes('already taken')) {
        toast.error('You have already taken this exam. Please check your results or contact your teacher.');
        navigate('/dashboard/exams');
      } else {
        toast.error('Failed to start exam. Please try again.');
      }
    }
  }

  const handleCancelExam = () => {
    navigate('/dashboard/exams')
  }

  const handleExitExam = () => {
    setShowExitWarning(true)
  }

  const handleConfirmExit = () => {
    navigate('/dashboard/exams')
  }

  const handleStayInExam = () => {
    setShowExitWarning(false)
  }

  const handleSubmitExam = async () => {
    try {
      await examService.submitExamAnswers(examId, { answers })
      
      // Show success message
      toast.success('Exam submitted successfully!')
      
      // Redirect to dashboard
      navigate('/dashboard')
    } catch (err) {
      console.error('Failed to submit exam:', err);
      toast.error('Failed to submit exam. Please try again.');
    }
  }

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const getTimeColor = (seconds) => {
    if (seconds < 300) return 'text-red-600' // Less than 5 minutes
    if (seconds < 900) return 'text-yellow-600' // Less than 15 minutes
    return 'text-gray-900'
  }

  if (loading || attemptLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error) {
    return <ErrorDisplay message={error} />
  }

  console.log('TakeExam - examId:', examId);
  console.log('TakeExam - exam:', exam);
  console.log('TakeExam - loading:', loading);
  console.log('TakeExam - error:', error);
  console.log('TakeExam - hasAttempted:', hasAttempted);
  console.log('TakeExam - attemptLoading:', attemptLoading);

  if (!exam) {
    return <ErrorDisplay message="Exam not found" />
  }

  // Check if student has already attempted this exam
  if (hasAttempted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Card className="shadow-lg">
            <CardHeader className="bg-red-600 text-white">
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Exam Already Taken
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-red-800 font-medium mb-2">You have already taken this exam.</p>
                  <p className="text-red-700 text-sm">
                    Each exam can only be taken once. Please check your results or contact your teacher if you have any questions.
                  </p>
                </div>
                <div className="flex justify-center space-x-3">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/dashboard/exams')}
                    className="px-6"
                  >
                    Back to Exams
                  </Button>
                  <Button 
                    onClick={() => navigate(`/dashboard/exams/results/${examId}`)}
                    className="bg-blue-600 hover:bg-blue-700 px-6"
                  >
                    View Results
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Show instructions screen first
  if (showInstructions) {
    return (
      <ExamInstructions 
        exam={exam}
        hasAttempted={hasAttempted}
        attemptLoading={attemptLoading}
        onStartExam={handleStartExam}
        onCancel={handleCancelExam}
      />
    )
  }

  if (!exam.questionPaper?.questions || exam.questionPaper.questions.length === 0) {
    return <ErrorDisplay message="No questions available for this exam" />
  }

  const currentQuestion = exam.questionPaper?.questions?.[currentQuestionIndex]
  const totalQuestions = exam.questionPaper?.questions?.length || 0
  const answeredQuestions = Object.keys(answers).length

  console.log('TakeExam - currentQuestion:', currentQuestion);
  console.log('TakeExam - currentQuestion.type:', currentQuestion?.type);
  console.log('TakeExam - currentQuestion.options:', currentQuestion?.options);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{exam.title}</h1>
            <p className="text-gray-600">{exam.subject?.name} - {exam.class?.name}</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-gray-500" />
              <span className={`text-lg font-mono font-bold ${getTimeColor(timeRemaining)}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {answeredQuestions}/{totalQuestions} answered
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExitExam}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Exit Exam
            </Button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Question Navigation Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Questions</h3>
          <div className="grid grid-cols-5 gap-2 mb-6">
            {exam.questionPaper?.questions?.map((question, index) => (
              <button
                key={question.id}
                onClick={() => handleGoToQuestion(index)}
                className={`
                  w-10 h-10 rounded-lg border-2 flex items-center justify-center text-sm font-medium
                  ${index === currentQuestionIndex 
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                    : answers[question.id] 
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }
                `}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-indigo-500 rounded"></div>
              <span>Current question</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Answered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-300 rounded"></div>
              <span>Not answered</span>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <Button
              onClick={() => handlePreviousQuestion()}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              className="w-full"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            <Button
              onClick={() => handleNextQuestion()}
              disabled={currentQuestionIndex === totalQuestions - 1}
              variant="outline"
              className="w-full"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold">
                    Question {currentQuestionIndex + 1} of {totalQuestions}
                  </h2>
                  <Badge variant="outline">
                    {currentQuestion.marks} marks
                  </Badge>
                  <Badge variant="secondary">
                    {(currentQuestion.type === 'objective' || currentQuestion.type === 'objectives') && 'Multiple Choice'}
                    {(currentQuestion.type === 'theory' || currentQuestion.type === 'theories') && 'Theory/Essay'}
                    {(currentQuestion.type === 'fill-in-gaps' || currentQuestion.type === 'fill-in-blanks') && 'Fill in the Gaps'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div>
                  <p className="text-lg text-gray-900 mb-4">{currentQuestion.question}</p>
                </div>

                {/* Render question based on type */}
                {(currentQuestion.type === 'objective' || currentQuestion.type === 'objectives') && (
                  <ObjectiveQuestion
                    question={currentQuestion}
                    answer={answers[currentQuestion.id]}
                    onAnswerChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
                  />
                )}

                {(currentQuestion.type === 'theory' || currentQuestion.type === 'theories') && (
                  <TheoryQuestion
                    question={currentQuestion}
                    answer={answers[currentQuestion.id]}
                    onAnswerChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
                  />
                )}

                {(currentQuestion.type === 'fill-in-gaps' || currentQuestion.type === 'fill-in-blanks') && (
                  <FillInGapsQuestion
                    question={currentQuestion}
                    answer={answers[currentQuestion.id]}
                    onAnswerChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={() => setShowSubmitDialog(true)}
              className="bg-red-600 hover:bg-red-700"
            >
              Submit Exam
            </Button>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      {showSubmitDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                Submit Exam
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                Are you sure you want to submit your exam? You have answered {answeredQuestions} out of {totalQuestions} questions.
              </p>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Once submitted, you cannot make any changes to your answers.
                </p>
              </div>
            </CardContent>
            <div className="flex justify-end space-x-3 p-6 pt-0">
              <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitExam} className="bg-red-600 hover:bg-red-700">
                Submit Exam
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Exit Warning Dialog */}
      {showExitWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="bg-red-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Exit Exam Warning
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-red-800 font-medium mb-2">Are you sure you want to exit the exam?</p>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• Your progress will be saved</li>
                    <li>• The timer will continue running</li>
                    <li>• You may not be able to resume if time runs out</li>
                    <li>• This action cannot be undone</li>
                  </ul>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Recommendation:</strong> Complete the exam to avoid any issues.
                  </p>
                </div>
              </div>
            </CardContent>
            <div className="flex justify-end space-x-3 p-6 pt-0">
              <Button variant="outline" onClick={handleStayInExam}>
                Stay in Exam
              </Button>
              <Button onClick={handleConfirmExit} className="bg-red-600 hover:bg-red-700">
                Exit Exam
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export default TakeExam